import React, {
  useContext,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { TextInput, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { v4 as uuidv4 } from "uuid";
import { notifications } from "@mantine/notifications";
import { useCreateStudy } from "@/hooks/apiHooks/useCreateStudy";
import { useCheckPerformanceStatus } from "@/hooks/apiHooks/useCheckPerformanceStatus";
import { useCreatePerformance } from "@/hooks/apiHooks/useCreatePerformance";
import { getFormStyles } from "@/helpers/getFormStyles";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { Validatable } from "@/pages/study/StudyStepper";
import { useHomePageContext } from "@/hooks/contextHooks/useHomePageContext";
import styles from "../../css/study.module.css";
import { CustomSwitch } from "@/components/Generics/CustomSwitch";
import { CustomCheckbox } from "@/components/Generics/CustomCheckbox";
import { RingLoader } from "react-spinners";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import { useAssociateStudyToProject } from "@/hooks/apiHooks/useAssociateStudyToProject";

const POLARPLOT_IP =
  process.env.REACT_APP_POLARPLOT_IP ||
  "https://polarplot.shiplify.io/polarplot";

type Props = {
  onComplete: () => void;
  userIp?: string;
};

const LaunchProcessStep = forwardRef<Validatable, Props>(
  ({ onComplete, userIp = "127.0.0.1" }, ref) => {
    const context = useContext(UserContext);
    const [transactionId, setTransactionId] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [resultLink, setResultLink] = useState<string | null>(null);
    const [mergedConfig, setMergedConfig] = useState<any>(null); // Store the mergedConfig
    const [isLaunching, setIsLaunching] = useState(false);
    const lockedShipId = context?.studyFormValues.lockedShips?.[0];
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const { mutateAsync: createStudy } = useCreateStudy();
    const { mutateAsync: checkPerformanceStatus } = useCheckPerformanceStatus();
    const { mutate: createPerformance } = useCreatePerformance();
    const { mutateAsync: associateStudy } = useAssociateStudyToProject();
    const { isDarkMode } = useDarkModeContext();
    const formStyles = getFormStyles(isDarkMode);
    const { ships } = useHomePageContext();
    const { colors } = useAppColors();

    const [wavesActivated, setWavesActivated] = useState(false);

    const form = useForm({
      initialValues: {
        email: "",
        receiveEmail: false,
      },
      validate: {
        email: (value: string) =>
          value && !/^\S+@\S+$/.test(value) ? "Invalid email" : null,
      },
    });

    useEffect(() => {
      if (transactionId) {
        intervalRef.current = setInterval(() => {
          checkCalculationStatus(transactionId);
        }, 5000);
      }

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }, [transactionId]);

    useEffect(() => {
      if (status === "Calculation complete") setIsLaunching(false);
    }, [status]);

    useImperativeHandle(ref, () => ({
      validate: () => {
        if (form.values.receiveEmail && !form.values.email) {
          notifications.show({
            title: "Validation Error",
            message: "Please provide your email address.",
            color: "red",
            autoClose: 4000,
            position: "top-center",
          });
          return false;
        }
        const validation = form.validate();
        if (validation.hasErrors) {
          notifications.show({
            title: "Validation Error",
            message: "Please fill in all required fields correctly.",
            color: "red",
            autoClose: 4000,
            position: "top-center",
          });
          return false;
        }
        return true;
      },
      handleLaunch: () => handleSubmit(form.values),
      handleVisualizePolar: () =>
        resultLink && window.open(resultLink, "_blank"),
      handleDownloadConfig: handleDownloadConfig,
      transactionId,
      status,
      resultLink,
      isLaunchDisabled: !!transactionId || status === "processing",
      isVisualizationDisabled: !resultLink,
      isDownloadDisabled: !mergedConfig,
    }));

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTransactionId(null);
    };

    const checkCalculationStatus = async (transactionId: string) => {
      try {
        const data = await checkPerformanceStatus(transactionId);
        if (data.status === "ready") {
          setStatus("Calculation complete");
          setResultLink(`${POLARPLOT_IP}/${data.hash}`);
          setIsLaunching(false);
          notifications.show({
            title: "Calculation Complete",
            message: "Your performance calculation is complete.",
            color: "green",
            autoClose: 4000,
            position: "top-center",
          });
          stopPolling();

          if (!lockedShipId) {
            notifications.show({
              title: "Error",
              message: "No locked ship selected.",
              color: "red",
              autoClose: 4000,
              position: "top-center",
            });
            setTransactionId(null);
            return;
          }
          onComplete();

          const vessel_type = ships.find(
            (ship) => ship.id === context?.studyFormValues.lockedShips?.[0],
          )?.vessel_type;

          const studyData = {
            experiment_name: context?.studyFormValues.studyName,
            vessel_type: vessel_type || "",
            vessel_size:
              context?.studyFormValues.vesselData.dtwin.vessel.DWT.toString(),
            wing_model_used: context?.studyFormValues.windPropellersLib,
            form_values: context?.studyFormValues,
            result_link: `${POLARPLOT_IP}/${data.hash}`,
            ship_id: lockedShipId,
            comment: context?.studyFormValues.comment || "",
          };

          const savedStudy = await createStudy(studyData);
          for (const pid of context?.studyFormValues.selectedProjects ?? []) {
            await associateStudy({ study: savedStudy, projectId: pid });
          }
        } else if (data.status === "processing") {
          setStatus("Calculation in progress...");
        }
      } catch (error) {
        setStatus("Error checking calculation status.");
        stopPolling();
        setIsLaunching(false);
      }
    };

    // ✅ FIX: build config like Dash (clone vesselData, patch dtwin.loads.wind_propulsion
    // ONLY if there are sails, and patch esperado_calculations.environment.* as arrays when waves disabled)
    const buildPerformanceConfig = ({
      vesselData,
      windPropellersLib,
      windPropellersConfiguration,
      wavesActivated,
      hasSails,
    }: {
      vesselData: any;
      windPropellersLib: any;
      windPropellersConfiguration: Record<string, any>;
      wavesActivated: boolean;
      hasSails: boolean;
    }) => {
      // deep clone to avoid mutating original vesselData in context
      const config =
        typeof structuredClone === "function"
          ? structuredClone(vesselData)
          : JSON.parse(JSON.stringify(vesselData));

      config.dtwin ??= {};
      config.dtwin.loads ??= {};

      // ✅ Only inject wind_propulsion if sails exist
      if (hasSails) {
        config.dtwin.loads.wind_propulsion = {
          indexed: true,
          wind_propellers_lib: windPropellersLib,
          wind_propellers_configuration: windPropellersConfiguration,
        };
      } else {
        // optional safety: ensure we don't send it at all
        delete config.dtwin.loads.wind_propulsion;
      }

      config.esperado_calculations ??= {};
      config.esperado_calculations.environment ??= {};

      if (!wavesActivated) {
        config.esperado_calculations.environment.Hs_m = [0];
        config.esperado_calculations.environment.WA_deg = [0];
      } else {
        // keep your existing behavior if backend supports it
        config.esperado_calculations.environment.Hs_m = {
          min: 0,
          max: 6,
          n: 7,
        };
        config.esperado_calculations.environment.WA_deg = {
          min: 0,
          max: 180,
          n: 13,
        };

        delete config.esperado_calculations.environment._Hs_m;
        delete config.esperado_calculations.environment._WA_deg;
      }

      return config;
    };

    const handleSubmit = async (values: typeof form.values) => {
      setIsLaunching(true);
      try {
        const sailsData = context?.studyFormValues.sailPositions || {};
        const windPropellersLib =
          context?.studyFormValues.windPropellersLib || {};
        const lockedShipId = context?.studyFormValues.lockedShips?.[0];

        if (!lockedShipId) {
          notifications.show({
            title: "Error",
            message: "No locked ship selected.",
            color: "red",
          });
          setIsLaunching(false);
          return;
        }

        const vesselData =
          context?.ships.find((ship) => ship.id === lockedShipId)
            ?.vessel_data || {};

        if (!vesselData?.dtwin?.loads) {
          notifications.show({
            title: "Error",
            message: "Invalid vessel data structure.",
            color: "red",
            autoClose: 4000,
            position: "top-center",
          });
          setIsLaunching(false);
          return;
        }

        const windPropellersConfiguration = Object.entries(sailsData).reduce(
          (acc, [key, sail]: any) => {
            acc[key] = {
              type: sail.type,
              position_m: {
                x: sail.position_m?.x || 0,
                y: sail.position_m?.y || 0,
                z: -27,
              },
            };
            return acc;
          },
          {} as {
            [key: string]: {
              type: string;
              position_m: { x: number; y: number; z: number };
            };
          },
        );

        // ✅ NEW: detect if we actually have sails
        const hasSails = Object.keys(windPropellersConfiguration).length > 0;

        // ✅ FIX: use Dash-like config construction (no manual dtwin rebuild)
        const config = buildPerformanceConfig({
          vesselData,
          windPropellersLib,
          windPropellersConfiguration,
          wavesActivated,
          hasSails,
        });

        setMergedConfig(config);

        const performanceCalculationInput = {
          config,
          transaction_id: uuidv4(),
          timestamp: new Date().toISOString().split(".")[0],
          user_ip: userIp || "127.0.0.1",
          user_email: values.receiveEmail ? values.email : "",
          experiment_name: context?.studyFormValues.studyName,
          persistent: true,
        };

        createPerformance(performanceCalculationInput, {
          onSuccess: () => {
            setTransactionId(performanceCalculationInput.transaction_id);
          },
          onError: () => {
            setIsLaunching(false);
          },
        });
      } catch (error) {
        notifications.show({
          title: "Error",
          message: "There was an error sending your request.",
          color: "red",
          autoClose: 4000,
          position: "top-center",
        });
        setIsLaunching(false);
      }
    };

    const handleDownloadConfig = () => {
      if (mergedConfig) {
        const jsonConfig = JSON.stringify(mergedConfig, null, 2);
        const blob = new Blob([jsonConfig], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `config-${transactionId}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    return (
      <div className={styles.container}>
        <div className={styles.innerContainer}>
          <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
            <CustomCheckbox
              label="Receive the performance report by email"
              {...form.getInputProps("receiveEmail", { type: "checkbox" })}
              disabled={isLaunching}
            />
            {form.values.receiveEmail && (
              <TextInput
                mt="md"
                label="Your Email"
                placeholder="you@example.com"
                {...form.getInputProps("email")}
                disabled={isLaunching}
                styles={formStyles}
              />
            )}
          </form>

          <div className={styles.box}>
            <CustomSwitch
              value={wavesActivated}
              action={() => setWavesActivated(!wavesActivated)}
              disabled={mergedConfig}
              type="waves"
            />
            <Text>
              {wavesActivated
                ? "Waves modelling activated ✔️"
                : "Activate Waves modelling"}
            </Text>
          </div>
        </div>

        {status === "Calculation in progress..." ? (
          <div className={styles.loaderContainer}>
            <RingLoader color={colors.brand} size={200} />
            <Text>Processing...</Text>
          </div>
        ) : null}

        {status === "Calculation complete" ? (
          <div className={styles.success}>
            <img src="/DiceLogo.png" alt="D-ice Logo" className={styles.logo} />
            <Text>Your study has been computed successfully!</Text>
          </div>
        ) : null}
      </div>
    );
  },
);

export default LaunchProcessStep;
